import React, { ReactNode, useEffect } from 'react';
import { onIdTokenChanged, User } from 'firebase/auth';
import { auth } from 'config/initAuth';
import { useRouter } from 'next/router';
import { PACKAGE_TOURS_IN_HOUSE } from 'utils/packageToursSwitch';

interface AuthContextValue {
  authUser: User | null;
  loading: boolean;
}

interface AuthContextProps {
  children: ReactNode;
}

export const AuthContext = React.createContext<AuthContextValue>({
  authUser: null,
  loading: false,
});

const protectedRoutes = ['/settings'];
const protectedRoutesWithUrlId = ['/bookings', '/notifications'];

export const useAuthContext = () => React.useContext(AuthContext);

export const AuthContextProvider = ({ children }: AuthContextProps) => {
  const [authUser, setAuthUser] = React.useState<null | User>(null);
  const [loading, setLoading] = React.useState(true);
  const router = useRouter();

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, user => {
      // The ID token is not kept here: it goes stale in a sleeping tab.
      // Requests fetch it when sent, via utils/authHeaders (WHIT-339).
      setAuthUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Redirect unauthorized user. A link to one package booking (?package=,
    // WHIT-483) opens "My bookings" as a flight order's ?id= does — there
    // only, and only while package tours are booked here.
    const id =
      router.query.id ??
      (PACKAGE_TOURS_IN_HOUSE && router.pathname === '/bookings'
        ? router.query.package
        : undefined);
    if (id) {
      if (typeof id !== 'string') {
        router.push('/');
      }
      return;
    }
    if (
      !loading &&
      protectedRoutes.includes(router.pathname) &&
      (!authUser?.uid || authUser?.isAnonymous)
    ) {
      router.push('/');
    }

    if (
      !loading &&
      protectedRoutesWithUrlId.includes(router.pathname) &&
      !id &&
      (!authUser?.uid || authUser?.isAnonymous)
    ) {
      router.push('/');
    }
  }, [router, authUser, loading]);

  return (
    <AuthContext.Provider value={{ authUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
